import React, { useState } from 'react';
import PropTypes from "prop-types";
import { object, string } from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  useTheme,
} from "@mui/material";
import {
  Plus,
  Image as ImageIcon,
  AlertCircle
} from 'lucide-react';
import {
  formatDateTimeForBackend,
  validateFileSize,
  validateFileTypes,
} from "../../utils/functions/Helpers";
import { useLoadingSpinner } from "../../utils/components/LoadingSpinnerProvider";
import FileChip from "../../utils/components/FileChip";
import { createGallery } from "./GalleryServices";
import { tokens } from "../../theme";

const gallerySchema = object().shape({
  id: string().notRequired(),
  title: string().required("Title is required"),
  category: string().required("Category is required"),
  description: string().required("Description is required").max(2000, "Description cannot be more than 2000 characters"),
});

const GalleryForm = (props) => {
  const {
    gallery,
    updateGallery,
    toggleModal,
    setState,
    isUpdating = false,
  } = props;

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [, setApiError] = useState(false);
  const setIsLoading = useLoadingSpinner();
  const [files, setFiles] = useState(gallery?.sharedMedia ? [gallery.sharedMedia] : []);
  const [fileTypeError, setFileTypeError] = useState("");
  const [fileSizeError, setFileSizeError] = useState("");

  const {
    clearErrors,
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(gallerySchema),
    defaultValues: {
      id: gallery?.id ?? "",
      title: gallery?.title ?? "",
      category: gallery?.category ?? "",
      description: gallery?.description ?? "",
    },
  });

  const handleCancel = () => {
    clearErrors();
    reset();
    toggleModal(false);
  };

  const onSubmit = async (data) => {
    const galleryData = { ...data };
    if (files.length === 0) {
      setFileTypeError("At least one media file is required");
      return;
    }

    validateFileTypes(files, setFileTypeError);
    validateFileSize(files, setFileSizeError);

    if (fileTypeError || fileSizeError) {
      return;
    }

    setIsLoading(true);
    setApiError(false);

    try {
      if (isUpdating) {
        galleryData.lastUpdated = formatDateTimeForBackend(new Date(gallery.lastUpdated));
        await updateGallery(galleryData, files, setState, setApiError, setIsLoading);
      } else {
        await createGallery(galleryData, files, setState, setApiError, setIsLoading);
      }
      // Rely on service to handle error state. If we get here without exception, assume close is safe.
      handleCancel();
    } catch (err) {
      console.error("Submission failed", err);
    }
  };

  const handleNewFile = (event) => {
    const fileList = event.target.files;
    setFiles((prevFiles) => [...prevFiles, ...Array.from(fileList)]);
  };

  const removeFile = (fileToRemove) => {
    setFiles((prevFiles) => prevFiles.filter((file) => file !== fileToRemove));
  };


  return (
    <Box
      sx={{
        bgcolor: theme.palette.mode === "dark" ? colors.primary[500] : "#FFFFFF",
        p: { xs: 3, md: 5 },
        borderRadius: "24px",
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Typography
              variant="h3"
              fontWeight="700"
              sx={{
                mb: 1,
                color: colors.grey[100],
                fontFamily: "Josefin Sans"
              }}
            >
              {isUpdating ? "Update Gallery Item" : "Add to Gallery"}
            </Typography>
            <Typography variant="body1" sx={{ color: colors.grey[300], mb: 3 }}>
              Enter the metadata and upload your media for the gallery showcase.
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Gallery Title"
              {...register("title")}
              error={!!errors.title}
              helperText={errors.title?.message}
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
                }
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Category"
              placeholder="e.g. Wedding, Birthday, Corporate"
              {...register("category")}
              error={!!errors.category}
              helperText={errors.category?.message}
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
                }
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Box
              sx={{
                p: 3,
                borderRadius: "16px",
                border: `2px dashed ${theme.palette.mode === "dark" ? colors.primary[300] : colors.grey[200]}`,
                bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.01)",
                textAlign: 'center'
              }}
            >
              <Typography variant="h6" fontWeight="600" sx={{ mb: 2, color: colors.grey[100] }}>
                Media Showcase
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3, justifyContent: 'center' }}>
                {files.length > 0 ? (
                  files.map((file, index) => (
                    <FileChip
                      key={`file-${index}`}
                      file={file}
                      onDelete={() => removeFile(file)}
                      mediaType="gallery"
                    />
                  ))
                ) : (
                  <Box display="flex" flexDirection="column" alignItems="center" py={2}>
                    <ImageIcon size={48} color={colors.grey[400]} style={{ marginBottom: '12px', opacity: 0.5 }} />
                    <Typography color={colors.grey[400]}>No media selected</Typography>
                  </Box>
                )}
              </Box>

              <Button
                component="label"
                variant="outlined"
                startIcon={<Plus size={18} />}
                sx={{
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: "600",
                  borderColor: colors.grey[300],
                  color: colors.grey[100],
                  "&:hover": {
                    borderColor: colors.greenAccent[500],
                    bgcolor: `${colors.greenAccent[500]}0D`
                  }
                }}
              >
                Choose Files
                <input
                  type="file"
                  hidden
                  accept="image/*,video/*"
                  multiple
                  onChange={handleNewFile}
                />
              </Button>

              {(fileTypeError || fileSizeError) && (
                <Box display="flex" alignItems="center" justifyContent="center" gap={1} mt={2} color={colors.redAccent[500]}>
                  <AlertCircle size={14} />
                  <Typography variant="caption" fontWeight="600">
                    {fileTypeError || fileSizeError}
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Short Description"
              {...register("description")}
              error={!!errors.description}
              helperText={errors.description?.message}
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
                }
              }}
            />
          </Grid>

          <Grid item xs={12} sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleCancel}
              sx={{
                py: 1.5,
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: "700",
                borderColor: colors.redAccent[500],
                color: colors.redAccent[500],
                "&:hover": {
                  borderColor: colors.redAccent[600],
                  bgcolor: `${colors.redAccent[500]}0D`
                }
              }}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              type="submit"
              variant="contained"
              sx={{
                py: 1.5,
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: "700",
                bgcolor: colors.greenAccent[500],
                color: theme.palette.mode === "dark" ? colors.primary[500] : colors.primary[100],
                "&:hover": {
                  bgcolor: colors.greenAccent[400],
                  boxShadow: `0 8px 16px ${colors.greenAccent[500]}33`
                }
              }}
            >
              {isUpdating ? 'Update Item' : 'Add to Gallery'}
            </Button>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

GalleryForm.propTypes = {
  gallery: PropTypes.object,
  mediaFiles: PropTypes.array,
  updateGallery: PropTypes.func,
  toggleModal: PropTypes.func,
  setState: PropTypes.func,
  isUpdating: PropTypes.bool,
};

export default GalleryForm;
